import * as THREE from "three";
import { RINGS, WORLD_CENTRE, WORLD_SIZE } from "@zend/content";
import type { WorldView } from "./types.js";
import { fieldSurface } from "../scene/fieldSurface.js";

const PALETTE = [0x77774e, 0x876749, 0x849890, 0x667671, 0x4f6250, 0x605347].map((colour) => new THREE.Color(colour));
const smooth = (v: number) => { const t = Math.max(0, Math.min(1, v)); return t * t * (3 - 2 * t); };
const noise = (x: number, y: number) => Math.sin(x * 0.071 + Math.sin(y * 0.037)) * Math.cos(y * 0.063 - Math.sin(x * 0.043));
const random = (x: number, y: number) => { const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453; return n - Math.floor(n); };

/** Continuous native geometry and vertex colours. Ring identity remains in the map data, not tile-sized steps. */
export function terrainSample(x: number, y: number): { height: number; colour: THREE.Color } {
  const distance = Math.hypot(x - WORLD_CENTRE, y - WORLD_CENTRE);
  const colour = PALETTE[0]!.clone();
  for (let index = 1; index < RINGS.length; index++) {
    const border = RINGS[index]!.maxDist;
    colour.lerp(PALETTE[index]!, smooth((border + 5 - distance) / 10));
  }
  const rolling = noise(x, y), detail = noise(x * 3.1, y * 3.1);
  colour.multiplyScalar(0.93 + rolling * 0.055 + detail * 0.025);
  // Kept below the picking plane and all authored POI/model feet.
  return { height: -0.105 + rolling * 0.035 + detail * 0.012, colour };
}

export const TERRAIN_SEGMENTS = 150;
export const TERRAIN_DETAIL_LIMIT = 240;

export class WorldTerrain {
  readonly group = new THREE.Group();
  readonly ground: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshStandardMaterial>;
  private readonly rocks: THREE.InstancedMesh;
  private readonly grass: THREE.InstancedMesh;
  private disposed = false;

  constructor() {
    this.group.name = "world-terrain";
    const geometry = new THREE.PlaneGeometry(WORLD_SIZE, WORLD_SIZE, TERRAIN_SEGMENTS, TERRAIN_SEGMENTS);
    geometry.rotateX(-Math.PI / 2); geometry.translate(WORLD_CENTRE - 0.5, 0, WORLD_CENTRE - 0.5);
    const positions = geometry.getAttribute("position"), colours = new Float32Array(positions.count * 3);
    for (let i = 0; i < positions.count; i++) {
      const sample = terrainSample(positions.getX(i), positions.getZ(i));
      positions.setY(i, sample.height); sample.colour.toArray(colours, i * 3);
    }
    geometry.setAttribute("color", new THREE.BufferAttribute(colours, 3)); geometry.computeVertexNormals();
    this.ground = new THREE.Mesh(geometry, fieldSurface(0xffffff));
    this.ground.name = "world-ground"; this.ground.receiveShadow = true;
    const stone = new THREE.IcosahedronGeometry(0.15, 1);
    this.rocks = new THREE.InstancedMesh(stone, new THREE.MeshStandardMaterial({ color: 0x8d9384, roughness: 1 }), TERRAIN_DETAIL_LIMIT);
    this.rocks.name = "world-terrain-rocks"; this.rocks.receiveShadow = true; this.rocks.castShadow = true;
    const leaves = new THREE.BufferGeometry();
    leaves.setAttribute("position",new THREE.Float32BufferAttribute([-.12,0,0,0,.36,0,.12,0,0,0,0,-.12,0,.27,0,0,0,.12],3));
    leaves.computeVertexNormals();
    this.grass = new THREE.InstancedMesh(leaves, new THREE.MeshStandardMaterial({ color: 0x777047, roughness: 1, side: THREE.DoubleSide }), TERRAIN_DETAIL_LIMIT);
    this.grass.name = "world-terrain-grass";
    this.rocks.count = this.grass.count = 0;
    this.group.add(this.ground, this.rocks, this.grass);
  }

  setView(view: WorldView): void {
    const occupied = new Set(view.tiles.map((tile) => `${tile.x},${tile.y}`));
    const transform = new THREE.Object3D(); let count = 0;
    // Stable world coordinates: no random re-seeding or ground patch moving with the camera.
    for (let y = Math.max(0, Math.floor((view.centre.y - view.radius) / 3) * 3); y <= Math.min(WORLD_SIZE - 1, view.centre.y + view.radius); y += 3) {
      for (let x = Math.max(0, Math.floor((view.centre.x - view.radius) / 3) * 3); x <= Math.min(WORLD_SIZE - 1, view.centre.x + view.radius); x += 3) {
        if (count >= TERRAIN_DETAIL_LIMIT || occupied.has(`${x},${y}`) || random(x, y) < 0.28) continue;
        const px = x + random(y, x) * 0.5, py = y + random(x + 3, y) * 0.5;
        transform.position.set(px, -0.03, py); transform.rotation.set(0.1, random(x, y) * 6.28, 0.12);
        const size = 0.65 + random(x + 1, y) * 0.7; transform.scale.set(size * 1.3, size * 0.65, size); transform.updateMatrix(); this.rocks.setMatrixAt(count, transform.matrix);
        transform.position.x += 0.21; transform.rotation.z = -0.2; transform.scale.setScalar(size); transform.updateMatrix(); this.grass.setMatrixAt(count, transform.matrix); count++;
      }
    }
    for (const mesh of [this.rocks, this.grass]) { mesh.count = count; mesh.instanceMatrix.needsUpdate = true; mesh.computeBoundingSphere(); }
  }

  setOverview(overview: boolean): void { this.rocks.visible = this.grass.visible = !overview; }
  dispose(): void {
    if (this.disposed) return; this.disposed = true;
    for (const mesh of [this.ground, this.rocks, this.grass]) { mesh.geometry.dispose(); (mesh.material as THREE.Material).dispose(); if (mesh instanceof THREE.InstancedMesh) mesh.dispose(); }
    this.group.removeFromParent(); this.group.clear();
  }
}
